import { Extension } from '@tiptap/core'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

const pluginKey = new PluginKey('saraMentionHighlight')

/** Matches @sara (incl. partial while typing), leading "sara", and `sara_` — kept in sync with routing triggers. */
const SARA_INPUT_HIGHLIGHT_PATTERNS = [
  /(?:^|\s)(@[Ss](?:[Aa](?:[Rr](?:[Aa])?)?)?)(?=\s|,|$|[^\w@])/g,
  /^(sara_)(?=\S|$)/gi,
  /^(sara)(?=[,\s]|$)/gi,
]

export const findSaraHighlightRanges = (
  text: string
): Array<{ from: number; to: number }> => {
  const ranges: Array<{ from: number; to: number }> = []
  const occupied = new Set<number>()

  const addRange = (start: number, end: number) => {
    for (let i = start; i < end; i++) {
      if (occupied.has(i)) return
    }
    for (let i = start; i < end; i++) occupied.add(i)
    ranges.push({ from: start, to: end })
  }

  for (const pattern of SARA_INPUT_HIGHLIGHT_PATTERNS) {
    const re = new RegExp(pattern.source, pattern.flags)
    let match: RegExpExecArray | null

    while ((match = re.exec(text)) !== null) {
      const token = match[1]
      if (!token) continue

      const start = match.index + match[0].indexOf(token)
      addRange(start, start + token.length)
    }
  }

  return ranges.sort((a, b) => a.from - b.from)
}

const textOffsetToPos = (doc: ProseMirrorNode, offset: number): number | null => {
  let current = 0
  let result: number | null = null

  doc.descendants((node, pos) => {
    if (result !== null) return false
    if (!node.isText || !node.text) return

    const len = node.text.length
    if (offset <= current + len) {
      result = pos + (offset - current)
      return false
    }
    current += len
  })

  return result
}

const buildDecorations = (doc: ProseMirrorNode): DecorationSet => {
  const text = doc.textContent
  if (!text) return DecorationSet.empty

  const decorations: Decoration[] = []

  for (const { from, to } of findSaraHighlightRanges(text)) {
    const fromPos = textOffsetToPos(doc, from)
    const toPos = textOffsetToPos(doc, to)
    if (fromPos == null || toPos == null || fromPos >= toPos) continue

    decorations.push(
      Decoration.inline(fromPos, toPos, {
        class: 'sara-mention-highlight',
      })
    )
  }

  return DecorationSet.create(doc, decorations)
}

export const SaraMentionHighlight = Extension.create({
  name: 'saraMentionHighlight',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: pluginKey,
        state: {
          init: (_, { doc }) => buildDecorations(doc),
          apply: (tr, decorationSet, _oldState, newState) => {
            if (tr.docChanged) {
              return buildDecorations(newState.doc)
            }
            return decorationSet.map(tr.mapping, tr.doc)
          },
        },
        props: {
          decorations(state) {
            return pluginKey.getState(state)
          },
        },
      }),
    ]
  },
})
